/**
 * Asset Creation Layout Route
 *
 * Layout route that provides the AssetCreationContext and WorkflowNavigationProvider.
 * Renders child step routes with a progress bar and workflow navigation.
 */

import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import {
  Button,
  Container,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import type { Id } from 'convex/_generated/dataModel'
import {
  AssetCreationProvider,
  useAssetCreation,
} from '@/features/assets/context/AssetCreationContext'
import {
  ProgressBar,
  WorkflowNavigation,
  WorkflowNavigationProvider,
  useWorkflowNavigation,
} from '@/components/common'

export const Route = createFileRoute('/_authed/assets/new')({
  component: AssetCreationLayoutRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    parentId: search.parentId as string | undefined,
  }),
})

function AssetCreationLayoutRoute() {
  const { parentId } = Route.useSearch()

  return (
    <AssetCreationProvider initialParentId={parentId as Id<'assetInventory'> | undefined}>
      <WorkflowNavigationProvider>
        <AssetCreationLayoutContent />
      </WorkflowNavigationProvider>
    </AssetCreationProvider>
  )
}

function AssetCreationLayoutContent() {
  const location = useLocation()
  const { parentId } = useAssetCreation()
  const { navigationState } = useWorkflowNavigation()

  // Determine current step from URL and calculate progress
  const currentPath = location.pathname
  const isSelectTypeStep = currentPath === '/assets/new' || currentPath === '/assets/new/'
  const isConfigureStep = currentPath.includes('/assets/new/configure')
  const isCompleteStep = currentPath.includes('/assets/new/complete')

  // Calculate progress: 33% for step 1, 66% for step 2, 100% for step 3
  const progress = isSelectTypeStep ? 33 : isConfigureStep ? 66 : isCompleteStep ? 100 : 0


  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Stack gap={4}>
            <Title order={1}>Add Asset</Title>
          </Stack>

        </Group>

        {/* Progress Bar */}
        {!isCompleteStep && <ProgressBar value={progress} size="sm" />}

        {/* Step Content - rendered by child routes */}
        <Outlet />

        {/* Workflow Navigation - positioned at bottom */}
        <WorkflowNavigation
          primaryButton={navigationState.primaryButton}
          secondaryButton={navigationState.secondaryButton}
          additionalButtons={navigationState.additionalButtons}
          show={navigationState.show}
        />
      </Stack>
    </Container>
  )
}
